import type { UserQuestionProps } from './UserQuestion';
import { UserQuestion } from './UserQuestion';
import type { GeniallyUser } from './GeniallyUser/GeniallyUser';

export class UserTrackingData {
  public readonly userAlias: string;

  public readonly hits: string;

  public readonly questions: UserQuestion[];

  public readonly questionsAttempts: UserQuestion[];

  public readonly totalAccess: number;

  public readonly activitiesDone: string;

  public readonly sessionDuration: number;

  public readonly visitedSlidesAmountOverTotalSlides: string;

  public readonly firstAccessDate: Date;

  public readonly geniallyUser?: GeniallyUser;

  private constructor(data: {
    userAlias: string;
    hits: string;
    questions: UserQuestion[];
    questionsAttempts: UserQuestion[];
    totalAccess: number;
    activitiesDone: string;
    sessionDuration: number;
    visitedSlidesAmountOverTotalSlides: string;
    firstAccessDate: Date;
    geniallyUser?: GeniallyUser;
  }) {
    this.userAlias = data.userAlias;
    this.hits = data.hits;
    this.questions = data.questions;
    this.questionsAttempts = data.questionsAttempts;
    this.totalAccess = data.totalAccess;
    this.activitiesDone = data.activitiesDone;
    this.sessionDuration = data.sessionDuration;
    this.visitedSlidesAmountOverTotalSlides = data.visitedSlidesAmountOverTotalSlides;
    this.firstAccessDate = data.firstAccessDate;
    this.geniallyUser = data.geniallyUser;
  }

  static of(data: UserTrackingDataProps): UserTrackingData {
    const userQuestions = data.questions.map(q =>
      UserQuestion.of({
        id: q.id,
        type: q.type,
        question: q.question,
        answer: q.answer,
        answersSelected: q.answersSelected,
        isCorrect: q.isCorrect,
        isTimeFinished: q.isTimeFinished,
        sentDate: q.sentDate,
        slideId: q.slideId,
        userAlias: q.userAlias,
        geniallyUser: data.geniallyUser,
      }),
    );

    const userQuestionsAttempts = data.questionsAttempts.map(q =>
      UserQuestion.of({
        id: q.id,
        type: q.type,
        question: q.question,
        answer: q.answer,
        answersSelected: q.answersSelected,
        isCorrect: q.isCorrect,
        sentDate: q.sentDate,
        slideId: q.slideId,
        userAlias: q.userAlias,
        geniallyUser: data.geniallyUser,
      }),
    );

    return new UserTrackingData({
      ...data,
      questions: userQuestions,
      questionsAttempts: userQuestionsAttempts,
    });
  }

  get nameOrAlias(): string {
    return this.geniallyUser?.isExistingUser()
      ? this.geniallyUser.fullName ?? ''
      : this.userAlias;
  }
}

export type UserTrackingDataProps = {
  userAlias: string;
  hits: string;
  questions: UserQuestionProps[];
  questionsAttempts: UserQuestionProps[];
  totalAccess: number;
  activitiesDone: string;
  sessionDuration: number;
  visitedSlidesAmountOverTotalSlides: string;
  firstAccessDate: Date;
  geniallyUser?: GeniallyUser;
};
